import {
	CATEGORY_FILTER_ID,
	type FilterConfig,
	type FilterConfigs,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';

export type CategoryFilterOption = FilterConfig['filterOptions'][number];

/**
 * Query param appended to a same-search location-change navigation's
 * destination URL (see LocationHeader's handleLocationChange). Its presence
 * tells useClassifierCategorySync — and, for the location-only route,
 * seedClassifierState — that the category state already reflects an explicit
 * choice for this exact search query (only the location changed), so the
 * backend classifier's own suggestion must not overwrite it (GS-1468).
 *
 * Consumed once and stripped from the URL by useClassifierCategorySync,
 * mirroring the semanticSuggestionCategoryCode marker used for typeahead
 * suggestion navigations.
 */
export const PRESERVE_CATEGORY_QUERY_PARAM = 'preserveCategory';

/**
 * Resolves classifier-selected category GUIDs against the category filter's
 * options. Returns `null` when the category filter isn't configured (cross-
 * category feature inactive) or when resolution is empty/partial — a partial
 * match would leave the UI/URL out of sync with what the backend actually
 * searched for, so callers should treat it the same as "nothing resolved".
 *
 * Shared between the client-side sync hook (useClassifierCategorySync) and
 * the SSR seeding step (seedClassifierState) so both compute the same result
 * for the same inputs.
 */
export function resolveClassifierCategoryOptions(
	selectedCategories: string[],
	filterConfigs: FilterConfigs,
): CategoryFilterOption[] | null {
	const categoryConfig = filterConfigs[CATEGORY_FILTER_ID];
	if (!categoryConfig) return null;

	const resolvedOptions = selectedCategories
		.map((guid) => categoryConfig.filterOptions.find((opt) => opt.id === guid))
		.filter((opt): opt is CategoryFilterOption => opt !== undefined);

	if (
		resolvedOptions.length === 0 ||
		resolvedOptions.length !== selectedCategories.length
	) {
		return null;
	}

	return resolvedOptions;
}

/**
 * Idempotency key identifying a single classifier result for a given page.
 * Deliberately excludes categoryId (see useClassifierCategorySync) — only
 * the classifier's own inputs/outputs, so the same key can be computed
 * server-side (seedClassifierState) and client-side (useClassifierCategorySync)
 * from equivalent request data.
 */
export function computeClassifierSyncKey(
	selectedCategories: string[],
	locationSlug: string,
	searchQuery: string,
): string {
	return JSON.stringify([
		selectedCategories.slice().sort(),
		locationSlug,
		searchQuery,
	]);
}
